
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, DollarSign, Users, Target, AlertTriangle } from 'lucide-react';

const kpiData = [
  {
    perspective: 'Financial',
    kpis: [
      { name: 'Program ROI', value: '23.5%', trend: 'up', change: '+2.1%', status: 'good' },
      { name: 'Cost per Point Redeemed', value: '$0.85', trend: 'down', change: '-5.2%', status: 'good' },
      { name: 'Liability Outstanding', value: '$2.1M', trend: 'up', change: '+12.3%', status: 'warning' },
      { name: 'Breakage Rate', value: '8.2%', trend: 'down', change: '-1.1%', status: 'good' }
    ]
  },
  {
    perspective: 'Customer',
    kpis: [
      { name: 'Enrollment Rate', value: '12.3%', trend: 'up', change: '+0.8%', status: 'good' },
      { name: 'Active Member Rate', value: '68%', trend: 'up', change: '+3.2%', status: 'good' },
      { name: 'Redemption Rate', value: '76%', trend: 'down', change: '-2.1%', status: 'warning' },
      { name: 'Retention Rate', value: '84%', trend: 'up', change: '+1.5%', status: 'good' },
      { name: 'Net Promoter Score', value: '52', trend: 'up', change: '+4', status: 'good' }
    ]
  },
  {
    perspective: 'Internal Process',
    kpis: [
      { name: 'Time to Launch Campaign', value: '4.2 hrs', trend: 'down', change: '-25%', status: 'good' },
      { name: 'Settlement Latency', value: '2.3s', trend: 'down', change: '-0.5s', status: 'good' },
      { name: 'Fraud Detection Coverage', value: '98.5%', trend: 'up', change: '+1.2%', status: 'good' },
      { name: 'API Error Rate', value: '0.03%', trend: 'down', change: '-0.01%', status: 'good' }
    ]
  },
  {
    perspective: 'Learning & Growth',
    kpis: [
      { name: 'AI-Agent Utilization', value: '45%', trend: 'up', change: '+12%', status: 'good' },
      { name: 'Forecast Accuracy', value: '92%', trend: 'up', change: '+3%', status: 'good' },
      { name: 'Model Retraining', value: 'Monthly', trend: 'stable', change: '0', status: 'good' },
      { name: 'Staff AI Satisfaction', value: '87%', trend: 'up', change: '+5%', status: 'good' }
    ]
  }
];

export function KPIDashboard() {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'danger': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <div className="h-4 w-4" />;
    }
  };

  const getPerspectiveIcon = (perspective: string) => {
    switch (perspective) {
      case 'Financial': return <DollarSign className="h-5 w-5" />;
      case 'Customer': return <Users className="h-5 w-5" />;
      case 'Internal Process': return <Target className="h-5 w-5" />;
      case 'Learning & Growth': return <AlertTriangle className="h-5 w-5" />;
      default: return <div className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {kpiData.map((section) => (
        <Card key={section.perspective}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getPerspectiveIcon(section.perspective)}
              {section.perspective} Perspective
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {section.kpis.map((kpi) => (
                <div key={kpi.name} className="p-4 border rounded-lg bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{kpi.name}</p>
                      <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                    </div>
                    {getTrendIcon(kpi.trend)}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge variant="secondary" className={getStatusColor(kpi.status)}>
                      {kpi.change}
                    </Badge>
                    <span className="text-xs text-gray-500">vs last period</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
